import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'

import { DetailHeaderAction } from '../models/detail-page.model'
import {
    DetailActionButtonComponent,
    DetailActionButtonFlavor
} from '../detail-action-button/detail-action-button.component'

/**
 * Renders an inline row of `DetailHeaderAction[]` as buttons. Used in
 * `<app-detail-header>` and exposed standalone so other surfaces (modal
 * footers, table cells, sidebar callouts) can reuse the same visual.
 *
 * Per-action `align` (`'start' | 'end'`) splits the row into a left
 * group and a right group separated by `justify-content: space-between`.
 * Common pattern: a "Cancel" button on the left while "Submit" sits on
 * the right of a modal footer.
 */
@Component({
    selector: 'app-detail-action-bar',
    standalone: true,
    imports: [CommonModule, DetailActionButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './detail-action-bar.component.html',
    styleUrls: ['./detail-action-bar.component.scss']
})
export class DetailActionBarComponent {
    @Input() actions: DetailHeaderAction[] = []
    @Input() testId?: string
    /** Default alignment for actions that don't set their own `align`. */
    @Input() align: 'start' | 'center' | 'end' = 'end'
    /** Visual flavor passed to each child button. */
    @Input() flavor: DetailActionButtonFlavor = 'bootstrap'
    @Output() action = new EventEmitter<DetailHeaderAction>()

    onClick(a: DetailHeaderAction): void {
        this.action.emit(a)
    }

    get leftActions(): DetailHeaderAction[] {
        return this.actions.filter(a => a.align === 'start')
    }

    /** Actions with no per-action `align` go in the bar's default-side
     *  bucket. So `align="end"` (default) keeps unaligned actions on the
     *  right; `align="start"` puts them on the left. */
    get rightActions(): DetailHeaderAction[] {
        return this.actions.filter(a => a.align === 'end' || (a.align === undefined && this.align !== 'start'))
    }

    /** When the bar's default is `start`, unaligned actions go in the
     *  left bucket. We model that by feeding them through `leftActions`'
     *  consumer-pattern path. */
    get unalignedLeftActions(): DetailHeaderAction[] {
        if (this.align !== 'start') return []
        return this.actions.filter(a => a.align === undefined)
    }

    get hasSplit(): boolean {
        return this.leftActions.length > 0 || this.unalignedLeftActions.length > 0
    }
}
